import { useQuery } from '@tanstack/react-query';
import { queryClient } from './queryClient';
import { patchCachedEntity } from './router';

// ── The normalized entity layer — the heart of the "sentient data" model ──────
//
// One cached truth per entity, populated by whoever loads it first. When ANY
// list query resolves it seeds each row into that entity's detail cache, so:
//   • opening an entity's detail is instant (no refetch if already seen),
//   • a reference from another entity (a payout's event, a txn's order/customer)
//     resolves from cache with no flicker and no cross-screen disagreement,
//   • a mutation updates the one cached copy and every view reflects it.
//
// `detailKey` is a query-key builder from the qk factory, e.g. `qk.events.detail`.

type DetailKey = (id: string) => readonly unknown[];

/** Seed per-id entity caches from a list result. Call from a list queryFn. */
export function seedEntities(detailKey: DetailKey, rows: any[] | undefined, idKey = 'id') {
  for (const row of rows || []) {
    const id = row?.[idKey];
    if (id != null) queryClient.setQueryData(detailKey(String(id)), row);
  }
}

type ListKey = readonly unknown[];

/**
 * Write-through a mutation result into BOTH the list cache and the per-id entity
 * cache, so every view reflects the change at once — no refetch, no cross-screen
 * disagreement. Merges `next` into the matching row/entity. Handles every list
 * cache shape we use: a bare `row[]`, a `{ rows: row[] }` page object, and a
 * named-key list like `{ agents }` / `{ boxOffices }` (patch the first array
 * property — same convention as entityById's extractRows).
 */
export function patchEntity(
  listKey: ListKey,
  detailKey: DetailKey,
  next: { [k: string]: any },
  idKey = 'id',
) {
  const id = next?.[idKey];
  if (id == null) return;
  const patchRow = (r: any) => (r?.[idKey] === id ? { ...r, ...next } : r);
  queryClient.setQueryData<any>(listKey, (data: any) => {
    if (Array.isArray(data)) return data.map(patchRow);
    if (Array.isArray(data?.rows)) return { ...data, rows: data.rows.map(patchRow) };
    if (data && typeof data === 'object') {
      // named-key list ({ agents }, { boxOffices }, …): patch the first array prop.
      const arrKey = Object.keys(data).find((k) => Array.isArray(data[k]));
      if (arrKey) return { ...data, [arrKey]: data[arrKey].map(patchRow) };
    }
    return data;
  });
  queryClient.setQueryData<any>(detailKey(String(id)), (old: any) => (old ? { ...old, ...next } : old));
  // And the route's own cached copy, which the tab title and breadcrumb read (router patchCachedEntity).
  patchCachedEntity(String(id), next);
}

/**
 * Prepend a newly-created entity to the list cache and seed its detail — the
 * create counterpart to patchEntity. De-dupes by id (so an echo from a later
 * refetch won't double it). Handles the same three list shapes. Use after a
 * create mutation that returns the new row, instead of refetching the list.
 */
export function prependEntity(listKey: ListKey, detailKey: DetailKey, row: any, idKey = 'id') {
  const id = row?.[idKey];
  if (id == null) return;
  const prepend = (rows: any[]) => [row, ...rows.filter((r) => String(r?.[idKey]) !== String(id))];
  queryClient.setQueryData<any>(listKey, (data: any) => {
    if (Array.isArray(data)) return prepend(data);
    if (Array.isArray(data?.rows)) return { ...data, rows: prepend(data.rows) };
    if (data && typeof data === 'object') {
      const arrKey = Object.keys(data).find((k) => Array.isArray(data[k]));
      if (arrKey) return { ...data, [arrKey]: prepend(data[arrKey]) };
    }
    return data;
  });
  queryClient.setQueryData(detailKey(String(id)), row);
}

/**
 * Remove an entity from the list cache and drop its detail entry — the delete
 * counterpart to patchEntity. Handles the same three list shapes (`row[]`,
 * `{ rows }`, named-key). Use after a successful delete mutation so the row
 * disappears with no full-list refetch.
 */
export function removeEntity(listKey: ListKey, detailKey: DetailKey, id: string, idKey = 'id') {
  if (id == null) return;
  const drop = (rows: any[]) => rows.filter((r) => String(r?.[idKey]) !== String(id));
  queryClient.setQueryData<any>(listKey, (data: any) => {
    if (Array.isArray(data)) return drop(data);
    if (Array.isArray(data?.rows)) return { ...data, rows: drop(data.rows) };
    if (data && typeof data === 'object') {
      const arrKey = Object.keys(data).find((k) => Array.isArray(data[k]));
      if (arrKey) return { ...data, [arrKey]: drop(data[arrKey]) };
    }
    return data;
  });
  queryClient.removeQueries({ queryKey: detailKey(String(id)) });
}

/** Read one entity already in cache (no fetch) — for resolving references. */
export function getEntity<T = any>(detailKey: DetailKey, id: string | null | undefined): T | undefined {
  if (id == null) return undefined;
  return queryClient.getQueryData<T>(detailKey(String(id)));
}

/**
 * Read an entity by id. Instant when a list (or a sibling view) already seeded
 * it; otherwise fetches via `fetchById`. Pass `fetchById = undefined` for
 * reference reads that should only ever come from cache (never trigger a fetch).
 */
export function useEntity<T = any>(
  detailKey: DetailKey,
  id: string | null | undefined,
  fetchById?: (id: string, opts: { signal?: AbortSignal }) => Promise<T>,
  opts: { enabled?: boolean; staleTime?: number } = {},
) {
  return useQuery<T>({
    queryKey: detailKey(String(id ?? '')),
    queryFn: fetchById ? ({ signal }) => fetchById(String(id), { signal }) : skipFetch,
    enabled: id != null && (opts.enabled ?? true),
    staleTime: opts.staleTime,
  });
}

// For reference-only reads (no fetchById): if the entity isn't seeded yet, resolve
// undefined instead of erroring — the referencing view shows a graceful fallback.
const skipFetch = async () => undefined as any;
